import {Component, OnInit} from '@angular/core';
import {TrackEditInfo} from "../model/track-edit-info";
import {MessageService} from "../service/message.service";
import {ActivatedRoute, Router} from "@angular/router";
import {ContentService} from "../service/content.service";
import {TrackService} from "../service/track.service";
import {ContentInfo} from "../model/content-info";
import {PlaylistInfo} from "../model/playlist-info";
import {PlaylistService} from "../service/playlist.service";
import {LoaderService} from "../service/loader.service";

@Component({
  selector: 'app-track-edit',
  templateUrl: './track-edit.component.html',
  styleUrls: ['./track-edit.component.css']
})
export class TrackEditComponent implements OnInit {

  private maxAudioSize = 50;
  private maxPictureSize = 10;
  private maxTitleLength = 256;
  private maxDescriptionLength = 2048;

  playlists: PlaylistInfo[] = [];

  trackId: string = null;
  trackEditInfo: TrackEditInfo = new TrackEditInfo(null, null, null, '', '', false, false, 0, []);
  audioFile: File = null;
  pictureFile: File = null;
  audioFilename: string;
  pictureFilename: string;

  titleFieldError: string = null;
  descriptionFieldError: string = null;
  pictureFileError: string = null;
  audioFileError: string = null;
  playlistsFieldError: string = null;
  attemptsFieldError: string = null;

  constructor(private messageService: MessageService,
              private trackService: TrackService,
              private playlistService: PlaylistService,
              private contentService: ContentService,
              private loaderService: LoaderService,
              private router: Router,
              private route: ActivatedRoute) {
  }

  ngOnInit() {
    let paramMap = this.route.snapshot.paramMap;
    this.playlistService.list().toPromise().then(v => {
      this.playlists = v.filter(pl => pl.is_public);
      this.initTrack(paramMap);
    }).catch(this.onRejected());
  }

  removePictureFile() {
    this.trackEditInfo.image_content = null;
    this.pictureFilename = null;
    this.pictureFile = null;
    this.pictureFileError = null;
  }

  removeAudioFile() {
    this.trackEditInfo.audio_content = null;
    this.audioFilename = null;
    this.audioFile = null;
    this.audioFileError = null;
  }

  onSubmit() {
    if (this.trackEditInfo.title == null || this.trackEditInfo.title.length == 0) {
      this.titleFieldError = "This field is required.";
    } else if (this.trackEditInfo.title.length > this.maxTitleLength) {
      this.titleFieldError = "You have exceeded the input length.";
    } else {
      this.titleFieldError = null;
    }

    if (this.trackEditInfo.description == null || this.trackEditInfo.description.length == 0) {
      this.descriptionFieldError = null; // not required
    } else if (this.trackEditInfo.description.length > this.maxDescriptionLength) {
      this.descriptionFieldError = "You have exceeded the input length.";
    } else {
      this.descriptionFieldError = null;
    }

    if (this.trackEditInfo.playlist_ids == null || this.trackEditInfo.playlist_ids.length == 0) {
      this.playlistsFieldError = "This field is required.";
    } else {
      this.playlistsFieldError = null;
    }

    if (this.trackEditInfo.is_bonus &&
      (this.trackEditInfo.bonus_questionnaire_attempts < 1 || this.trackEditInfo.bonus_questionnaire_attempts > 100)) {
      this.attemptsFieldError = "Value must be in range 1 - 100";
    } else {
      this.attemptsFieldError = null;
    }

    if (this.audioFileError == null && this.audioFile == null && this.audioFilename == null) {
      this.audioFileError = "This field is required.";
    }

    if (this.pictureFileError == null && this.pictureFile == null && this.pictureFilename == null) {
      this.pictureFileError = "This field is required.";
    }

    if (this.titleFieldError != null
      || this.descriptionFieldError != null
      || this.attemptsFieldError != null
      || this.audioFileError != null
      || this.pictureFileError != null
      || this.playlistsFieldError != null) {
      return;
    }

    // In case when user opened track for edit and not modified files
    let audioPromise = Promise.resolve(null);
    if (this.audioFile != null || this.audioFilename == null) {
      audioPromise = this.uploadFile(this.audioFile)
        .then(value => this.trackEditInfo.audio_content = value);
    }

    let picturePromise = Promise.resolve(null);
    if (this.pictureFile != null || this.pictureFilename == null) {
      picturePromise = this.uploadFile(this.pictureFile)
        .then(value => this.trackEditInfo.image_content = value);
    }

    let allPromises = [audioPromise, picturePromise];
    Promise.all(allPromises)
      .then(() => {
        this.saveTrack();
      })
      .catch(this.onRejected());
  }

  onAudioFileInputChange(files: FileList) {
    this.audioFile = files.item(0);
    this.audioFilename = this.audioFile.name;
    if (this.audioFile.size > this.maxAudioSize * 1048576) {
      this.audioFileError = `You have exceeded the maximum allowable file size (${this.maxAudioSize} mb).`;
    } else {
      this.audioFileError = null;
    }
  }

  onPictureFileInputChange(files: FileList) {
    this.pictureFile = files.item(0);
    this.pictureFilename = this.pictureFile.name;
    if (this.pictureFile.size > this.maxPictureSize * 1048576) {
      this.pictureFileError = `You have exceeded the maximum allowable file size (${this.maxPictureSize} mb).`;
    } else {
      this.pictureFileError = null;
    }
  }

  onChangeIsPaid() {
    if (this.trackEditInfo.is_paid) {
      this.trackEditInfo.is_bonus = false;
    }
  }

  onChangeIsBonus() {
    if (this.trackEditInfo.is_bonus) {
      this.trackEditInfo.is_paid = false;
    }
  }

  private initTrack(paramMap) {
    if (paramMap.has("id")) {
      this.trackId = paramMap.get("id");
      this.trackService.getTrackEdit(this.trackId)
        .then(value => {
          this.trackEditInfo = value;
          this.updateByTrack();
        })
        .catch(this.onRejected())
    } else {
      this.updateByTrack();
    }
  }

  private saveTrack() {
    let savePromise: Promise<any> = this.trackId == null ?
      this.trackService.create(this.trackEditInfo) :
      this.trackService.update(this.trackEditInfo);
    savePromise.then(() => {
      this.router.navigate(['/tracks']);
    }).catch(this.onRejected());
  }

  private uploadFile(file): Promise<ContentInfo> {
    if (file == null || file.size === 0) {
      return Promise.resolve(null);
    }
    let formData = new FormData();
    formData.append("file", file.slice(0, file.size, file.type), file.name);
    return this.contentService.upload(formData).toPromise();
  }

  private updateByTrack() {
    if (this.trackEditInfo.audio_content != null) {
      this.audioFilename = this.trackEditInfo.audio_content.original_name;
    }
    if (this.trackEditInfo.image_content != null) {
      this.pictureFilename = this.trackEditInfo.image_content.original_name;
    }
  }

  private onRejected() {
    return err => {
      this.messageService.addError('error occurred', err);
      console.log(err);
    }
  }
}



// WEBPACK FOOTER //
// /home/developer/Downloads/SleepRestoreBackend/sleep-restore-boot/src/main/ngapp/src/app/track-edit/track-edit.component.ts